import { includes } from 'lodash';
import {
  actionChangeOverlay,
  FACE_HIGHLIGHT,
  FACES_SELECT_GROUP_FACES,
  NEW_OVERLAY,
  UPDATE_CURRENT_TIME_MEDIA_PLAYER,
  RESIZE_OVERLAY_SEGMENT,
  sprayPaintSaveAction,
  deleteDetectionOverlayAction,
  deleteDetectionInFrameAction,
  deleteUDROverlayAction,
  deleteDetectionSegmentAction,
  deleteDetectionGroupAction,
  deleteClusterAction,
  deleteUDRInFrameAction,
  deleteUDRSegmentAction,
  deleteUDRGroupAction,
  actionUpdateStartEndLiveTrackingUdr,
  actionSetSelectedGroupsByGroupId,
  actionSetSelectedGroups,
  ChangeShapeRequest,
  BulkRedactRequest,
  ChangeRedactionRequest,
  ChangeCodeRequest,
  ChangeTextOverlayRequest,
  actionSeekPrevMarkerTime,
  GroupIdAndType,
  actionSetMergeGroupId,
  actionSetMergeSelectClusterIds,
  DeleteSegmentPayload,
  actionChangeUDRsPolyAssetGroupSeriesItemSubmit,
  actionChangeUDRsPolyAssetGroupSeriesItem,
  addTextOverlaySegmentAction,
  mergeTextOverlaySegmentsAction,
  setTextOverlaySingleRangeAction,
  BulkAddBackgroundBlurRequest,
  UpdateBackgroundBlurLevelsRequest,
  UPDATE_BACKGROUND_BLUR_LEVELS,
  SET_FILL_BADGE_EDIT_ANCHOR,
} from '@common-modules/mediaDetails';
import { markWorkerAction } from '@utils';
import {
  BULK_ADD_BACKGROUND_BLUR,
  CHANGE_CODE,
  CHANGE_SHAPE,
  CHANGE_TEXT_OVERLAY,
  GroupedBoundingPoly,
  SET_DETECTION_TYPE_REDACTION,
  SET_INDIVIDUAL_REDACTION,
} from '@worker';
import {
  BoundingPolyRect,
  FillBadgeEditAnchor,
  TextOverlay,
} from '@common-modules/mediaDetails/models';
import { DETECTION_TYPES } from '@helpers/constants';
import { processEngineRequestWithOverlayAction } from '@common-modules/engines/optical-tracking';
import { ActionType } from '../../types';

export const componentActions = {
  onProcessEngineRequestWithOverlayAction:
    processEngineRequestWithOverlayAction,
  onUpdateCurrentTime: (currentPosition: number) =>
    UPDATE_CURRENT_TIME_MEDIA_PLAYER({ currentPosition }),
  onDeleteBoundingBox: (boundingBox: GroupedBoundingPoly) => {
    if (includes(DETECTION_TYPES, boundingBox.type)) {
      return deleteDetectionOverlayAction(boundingBox);
    } else {
      return deleteUDROverlayAction(boundingBox);
    }
  },
  onDeleteBoundingBoxInFrame: (
    boundingBox: GroupedBoundingPoly,
    timeMs: number
  ) => {
    if (includes(DETECTION_TYPES, boundingBox.type)) {
      return deleteDetectionInFrameAction(boundingBox, timeMs);
    } else {
      return deleteUDRInFrameAction(boundingBox, timeMs);
    }
  },
  onDeleteSegment: (payload: DeleteSegmentPayload) => {
    if (includes(DETECTION_TYPES, payload.type)) {
      return deleteDetectionSegmentAction(payload);
    } else {
      return deleteUDRSegmentAction(payload);
    }
  },
  onDeleteBoundingBoxGroup: (boundingBox: GroupedBoundingPoly) => {
    if (includes(DETECTION_TYPES, boundingBox.type)) {
      return deleteDetectionGroupAction(boundingBox);
    } else {
      return deleteUDRGroupAction(boundingBox);
    }
  },
  onDeleteCluster: (
    clusterId: string,
    udrGroupIds: string[],
    detectionGroups: GroupIdAndType[]
  ) => deleteClusterAction(clusterId, udrGroupIds, detectionGroups),
  onAddBoundingBox: (
    payload: {
      boundingPoly: BoundingPolyRect;
      id: string;
      groupId: string;
      textOverlay?: TextOverlay;
      startTimeMs?: number;
      stopTimeMs?: number;
    },
    currentPosition: number,
    opts: { shift: boolean } = { shift: false }
  ) =>
    markWorkerAction(
      NEW_OVERLAY({
        ...payload,
        currentPosition,
        ...opts,
      })
    ),
  actionChangeUDRsPolyAssetGroupSeriesItem:
    actionChangeUDRsPolyAssetGroupSeriesItem,
  addTextOverlaySegment: addTextOverlaySegmentAction,
  mergeTextOverlaySegments: mergeTextOverlaySegmentsAction,
  setTextOverlaySingleRange: setTextOverlaySingleRangeAction,
  actionChangeUDRsPolyAssetGroupSeriesItemSubmit:
    actionChangeUDRsPolyAssetGroupSeriesItemSubmit,
  actionUpdateStartEndLiveTrackingUdr: actionUpdateStartEndLiveTrackingUdr,
  onChangeOverlay: actionChangeOverlay,
  onSelectGroupFaces: (payload: GroupedBoundingPoly) =>
    FACES_SELECT_GROUP_FACES({ groupId: payload.groupId }),
  onFaceHighlight: (payload: GroupedBoundingPoly) =>
    FACE_HIGHLIGHT({
      id: payload.id,
      timeMs: payload.startTimeMs,
      type: payload.type,
      groupId: payload.groupId,
    }),
  setSelectedGroupsByGroupId: actionSetSelectedGroupsByGroupId,
  setSelectedGroups: actionSetSelectedGroups,
  setMergeGroupId: actionSetMergeGroupId,
  setMergeClusterId: actionSetMergeSelectClusterIds,
  onResizeBoundingBoxSegment: (boundingBox: GroupedBoundingPoly) =>
    markWorkerAction(RESIZE_OVERLAY_SEGMENT({ boundingBox })),
  onSprayPaintSave: (payload: {
    id: string;
    groupId: string;
    series: { boundingPoly: BoundingPolyRect; time: number }[];
    actionType?: ActionType;
    isResizeByKeyboard?: boolean;
    suppressHighlight?: boolean;
  }) => sprayPaintSaveAction(payload),
  onBoxShapeChange: (payload: ChangeShapeRequest) =>
    markWorkerAction(CHANGE_SHAPE(payload)),
  onBoxCodeChange: (payload: ChangeCodeRequest) =>
    markWorkerAction(CHANGE_CODE(payload)),
  onBoxTextOverlayChange: (payload: ChangeTextOverlayRequest) =>
    markWorkerAction(CHANGE_TEXT_OVERLAY(payload)),
  onBoxRedactionChange: (payload: ChangeRedactionRequest) =>
    markWorkerAction(SET_INDIVIDUAL_REDACTION(payload)),
  onDetectionTypeRedactionChange: (payload: BulkRedactRequest) =>
    markWorkerAction(SET_DETECTION_TYPE_REDACTION(payload)),
  onBulkAddBackgroundBlur: (payload: BulkAddBackgroundBlurRequest) =>
    markWorkerAction(BULK_ADD_BACKGROUND_BLUR(payload)),
  onUpdateBackgroundBlurLevels: (payload: UpdateBackgroundBlurLevelsRequest) =>
    UPDATE_BACKGROUND_BLUR_LEVELS(payload),
  onSeekPrevMarkerTime: (timeMs: number) => actionSeekPrevMarkerTime(timeMs),
  setFillBadgeEditAnchor: (payload: FillBadgeEditAnchor | null) =>
    SET_FILL_BADGE_EDIT_ANCHOR(payload),
};
